import { ChartDataAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import {
  BarChartData,
  CategoryValue
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import {
  CategoryType,
  VariationsByCategoryData,
  GetVariationsMetricsByCategoryResponse,
  VariationsMetricsByCategoryRequestType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const MAX_PAGE_DENSITY = 1.0;
const MAX_ACCURACY_PERCENTAGE = 100;

const PERCENTAGE_REQUEST_TYPES = [
  VariationsMetricsByCategoryRequestType.VariationAccuracyByCategory,
  VariationsMetricsByCategoryRequestType.ChildAccuracyByCategory,
  VariationsMetricsByCategoryRequestType.PageDensityByCategory,
  VariationsMetricsByCategoryRequestType.ParentPageAccuracyByCategory
];

export class VariationsByCategoryAdapter implements ChartDataAdapter<BarChartData> {
  private data: VariationsByCategoryData;
  private categoryType: CategoryType;
  private displayNames: {
    category: Record<number, string>;
    subcategory: Record<number, string>;
    brand: Record<number, string>;
  };
  private requestType: VariationsMetricsByCategoryRequestType;

  constructor(
    categoryType: CategoryType,
    data: VariationsByCategoryData,
    displayNames: {
      category: Record<number, string>;
      subcategory: Record<number, string>;
      brand: Record<number, string>;
    },
    requestType: VariationsMetricsByCategoryRequestType
  ) {
    this.data = data;
    this.categoryType = categoryType;
    this.displayNames = displayNames;
    this.requestType = requestType;
  }

  private processMetricValue(value: number): number {
    const isPercentageMetric = PERCENTAGE_REQUEST_TYPES.includes(this.requestType);

    if (isPercentageMetric) {
      if (this.requestType === VariationsMetricsByCategoryRequestType.PageDensityByCategory) {
        return Math.min(value, MAX_PAGE_DENSITY);
      } else if (
        this.requestType === VariationsMetricsByCategoryRequestType.ChildAccuracyByCategory ||
        this.requestType === VariationsMetricsByCategoryRequestType.VariationAccuracyByCategory
      ) {
        return Math.min(value, MAX_ACCURACY_PERCENTAGE);
      }
    }

    return value;
  }

  private getCategoryName(id: number): string {
    switch (this.categoryType) {
      case CategoryType.Category:
        return this.displayNames.category[id];
      case CategoryType.Subcategory:
        return this.displayNames.subcategory[id];
      case CategoryType.Brand:
        return this.displayNames.brand[id];
      default:
        return id.toString();
    }
  }

  private getMetricType(): METRICTYPE {
    return PERCENTAGE_REQUEST_TYPES.includes(this.requestType) ? METRICTYPE.PERCENT : METRICTYPE.VOLUME;
  }

  transform(): BarChartData {
    const { mainRecords, comparisonRecords } = this.data;

    const allEntityIdsSet = new Set<number>();
    [...mainRecords, ...comparisonRecords].forEach((record) => {
      allEntityIdsSet.add(record.entityId);
    });

    const allCategoryIds = Array.from(allEntityIdsSet);

    const categories = allCategoryIds.map((id) => ({
      id,
      name: this.getCategoryName(id),
      type: this.categoryType.toLowerCase()
    }));

    const categoryMap = categories.reduce((acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    }, {});

    const result = [comparisonRecords, mainRecords].map((records) => {
      return {
        categories,
        data: this.createDataArray(records, categoryMap, allCategoryIds),
        metricType: this.getMetricType(),
        type: 'column'
      };
    });

    return { chartData: result, categoryType: this.categoryType };
  }

  private createDataArray(
    records: GetVariationsMetricsByCategoryResponse['data']['records'],
    categoryMap: Record<number, string>,
    allCategoryIds: number[]
  ): CategoryValue[] {
    const getMetricValueForCategory = (categoryId: number): number => {
      const record = records.find((record) => record.entityId === categoryId);
      return record ? this.processMetricValue(record.metricValue) : 0;
    };

    const mapCategoryToNameAndValue = (categoryId: number): CategoryValue => {
      const categoryName = categoryMap[categoryId];
      const metricValue = getMetricValueForCategory(categoryId);
      return [categoryName, metricValue];
    };

    return allCategoryIds.map(mapCategoryToNameAndValue);
  }
}
